import React, { FC, useEffect, useMemo, useState } from "react";
import { Button, Form, InputNumber, Switch } from "antd";
import { ClientFilterData, ClientValue, NewClientValue } from "../../data-types";
import styles from "./client-settings.css";
import { ClientAutoComplete } from "../client-auto-complete";

const {
  ["container"]: containerClassName,
  ["form"]: formClassName,
  ["button-row"]: buttonRowClassName,
  ["submit-button"]: submitButton,
} = styles as any;

export type ClientSettingsProps = {
  clientFilterData: ClientFilterData;
  onUpdateClientExcludeTax: (clientData: {
    clientId: number;
    excludeTax: boolean;
    gpmDangerThreshold: number | null;
    gpmWarningThreshold: number | null;
  }) => void;
  onClientSave: (client: NewClientValue) => void;
  loading: boolean;
};

export const ClientSettings: FC<ClientSettingsProps> = ({
  clientFilterData,
  onUpdateClientExcludeTax,
  onClientSave,
  loading: dataLoading,
}) => {
  const [selectedClient, setSelectedClient] = useState<ClientValue | undefined>(undefined);
  const [gpmDangerValue, setGpmDangerValue] = useState<boolean>(false);

  const [form] = Form.useForm();

  const initialValues = useMemo(() => {
    return {
      client: selectedClient ? selectedClient : undefined,
      excludeTax: selectedClient ? selectedClient?.excludeTax : false,
      gpmDangerThreshold: selectedClient?.gpmDangerThreshold,
      gpmWarningThreshold: selectedClient?.gpmWarningThreshold,
    };
  }, [selectedClient]);

  const onCancel = () => {
    form.resetFields();
  };

  const [formLoading, setFormLoading] = useState<boolean>(false);

  const loading = formLoading || dataLoading;

  const doOnFinish = (rawValues: any) => {
    try {
      setFormLoading(true);
      const values = {
        clientId: rawValues.client.id,
        excludeTax: rawValues.excludeTax,
        gpmDangerThreshold: rawValues.gpmDangerThreshold,
        gpmWarningThreshold: rawValues.gpmWarningThreshold,
      };
      onUpdateClientExcludeTax(values);
    } finally {
      setFormLoading(false);
    }
  };

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  return (
    <div className={containerClassName}>
      <Form
        initialValues={initialValues}
        form={form}
        labelAlign="left"
        labelCol={{ span: 7 }}
        wrapperCol={{ span: 17, offset: 1 }}
        className={formClassName}
        onFinish={doOnFinish}
      >
        <Form.Item noStyle shouldUpdate>
          {() => {
            return (
              <Form.Item
                label="Client"
                name="client"
                trigger="onChangeClient"
                rules={[
                  {
                    required: true,
                    message: "Please select a Client",
                  },
                ]}
              >
                <ClientAutoComplete
                  onClientSave={onClientSave || (() => {})}
                  clientFilterData={clientFilterData}
                  disabled={loading}
                  onChangeClient={(v) => {
                    setSelectedClient(v);
                  }}
                  bordered
                />
              </Form.Item>
            );
          }}
        </Form.Item>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            return getFieldValue("client")?.id ? (
              <Form.Item
                label="Tax exempt?"
                name="excludeTax"
                valuePropName="checked"
                rules={[
                  {
                    required: true,
                    message: "Should this Client be tax exempt?",
                  },
                ]}
              >
                <Switch disabled={loading} />
              </Form.Item>
            ) : null;
          }}
        </Form.Item>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            return getFieldValue("client")?.id ? (
              <Form.Item label="GPM Required" name="gpmDangerThreshold">
                <InputNumber<number>
                  min={0}
                  max={100}
                  formatter={(value) => (value ? `${value}%` : "")}
                  parser={(value: any) => value && value.replace("%", "")}
                  onChange={(value) => {
                    if (value || value === 0) {
                      setGpmDangerValue(true);
                    } else {
                      setGpmDangerValue(false);
                      form.setFieldsValue({ gpmWarningThreshold: null });
                    }
                  }}
                />
              </Form.Item>
            ) : null;
          }}
        </Form.Item>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            return getFieldValue("client")?.id ? (
              <Form.Item
                label="GPM Warning Threshold"
                name="gpmWarningThreshold"
                rules={[
                  {
                    required: gpmDangerValue,
                    message: "Please enter the Gross Profit Margin (GPM) Warning threshold",
                  },
                ]}
              >
                <InputNumber<number>
                  min={0}
                  max={100}
                  formatter={(value) => (value ? `${value}%` : "")}
                  parser={(value: any) => value && value.replace("%", "")}
                />
              </Form.Item>
            ) : null;
          }}
        </Form.Item>
        <Form.Item className={buttonRowClassName} shouldUpdate>
          {({ getFieldValue }) => {
            const valuesSet = !!getFieldValue("client")?.id;
            return (
              <>
                <Button
                  type="primary"
                  htmlType="submit"
                  className={submitButton}
                  disabled={loading || !valuesSet}
                >
                  Save
                </Button>
                <Button onClick={() => onCancel()}>Cancel</Button>
              </>
            );
          }}
        </Form.Item>
      </Form>
    </div>
  );
};
